/**
 * ObjectTypeFormFields Component
 * Renders the form fields for creating/editing object types
 */

import type { ObjectTypeKind, Property } from '../../../../../map-editor-shared/store/objectTypeStore'
import type { FormData } from '../hooks/useObjectTypeForm'
import { AssetUploader, ColorPicker, PropertyEditor, JsonPreview, IconSelector } from '../../components'
import styles from '../ObjectTypeSidebar.module.css'

const KIND_OPTIONS: { value: ObjectTypeKind; label: string }[] = [
  { value: 'asset', label: '에셋 이미지' },
  { value: 'label', label: '텍스트 라벨' },
  { value: 'line', label: '선' },
]

interface ObjectTypeFormFieldsProps {
  isEditing: boolean
  editingId: string | null
  formData: FormData
  setFormData: React.Dispatch<React.SetStateAction<FormData>>
  // Asset upload
  previewUrl: string | null
  assetFile: File | undefined
  showIconSelector: boolean
  setShowIconSelector: (show: boolean) => void
  fileInputRef: React.RefObject<HTMLInputElement | null>
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: (e: React.MouseEvent) => void
  onIconSelect: (path: string) => void
  // Property editor
  onAddProperty: () => void
  onUpdateProperty: (index: number, updates: Partial<Property>) => void
  onRemoveProperty: (index: number) => void
  // Actions
  onSave: () => void
  onCancel: () => void
  // Immediate color+mode sync (for editing existing types)
  onColorSync?: (updates: { color?: string; strokeColor?: string; fillColor?: string; opacity?: number; strokeWidth?: number; colorMode?: 'stroke' | 'fill' }) => void
  // JSON preview
  showJson: boolean
  setShowJson: (show: boolean) => void
  getPreviewData: () => Record<string, any>
}

export function ObjectTypeFormFields({
  isEditing,
  formData,
  setFormData,
  previewUrl,
  assetFile,
  showIconSelector,
  setShowIconSelector,
  fileInputRef,
  onFileChange,
  onRemoveFile,
  onIconSelect,
  onAddProperty,
  onUpdateProperty,
  onRemoveProperty,
  onSave,
  onCancel,
  onColorSync,
  showJson,
  setShowJson,
  getPreviewData,
}: ObjectTypeFormFieldsProps) {
  const hasActualImageIcon = previewUrl ||
    formData.icon?.startsWith('data:image/') ||
    formData.icon?.startsWith('/assets/') ||
    formData.icon?.startsWith('http')

  const kind = formData.kind || 'asset'

  return (
    <div className={styles.form}>
      {/* Kind selector: 에셋 이미지 / 텍스트 라벨 / 선 */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
        {KIND_OPTIONS.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, kind: option.value }))}
            className={styles.smallButton}
            style={{
              flex: 1,
              padding: '6px 4px',
              fontSize: '12px',
              background: kind === option.value ? 'var(--local-accent)' : 'var(--local-surface-hover)',
              color: kind === option.value ? 'white' : 'var(--local-text)',
              border: '1px solid var(--local-border)',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      <input
        type="text"
        placeholder="타입명 (영어, 예: EXIT)"
        value={formData.name}
        onChange={(e) => {
          // 영어/숫자/공백/하이픈/언더스코어만 입력 허용
          const name = e.target.value.replace(/[^A-Za-z0-9 _-]/g, '')
          setFormData(prev => ({ ...prev, name }))
        }}
        className={styles.input}
      />

      {/* Label kind: 텍스트 색상 / 폰트 크기 (박스 크기는 텍스트에 자동 맞춤) */}
      {kind === 'label' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '8px' }}>
          <div>
            <label style={{ fontSize: '12px', color: 'var(--local-text-secondary)', marginBottom: '4px', display: 'block' }}>텍스트 색상</label>
            <ColorPicker
              value={formData.color}
              onChange={(color) => {
                setFormData(prev => ({ ...prev, color, strokeColor: color }))
                if (onColorSync) onColorSync({ color, strokeColor: color })
              }}
            />
          </div>
          <div>
            <label className={styles.label}>폰트 크기 (px)</label>
            <input
              type="number"
              min={4}
              max={200}
              value={formData.fontSize}
              onChange={(e) => setFormData(prev => ({ ...prev, fontSize: Math.max(4, Number(e.target.value) || 12) }))}
              className={styles.input}
            />
          </div>
        </div>
      )}

      {/* Line kind: 선 색상 / 선 두께 */}
      {kind === 'line' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '8px' }}>
          <div>
            <label style={{ fontSize: '12px', color: 'var(--local-text-secondary)', marginBottom: '4px', display: 'block' }}>선 색상</label>
            <ColorPicker
              value={formData.strokeColor}
              onChange={(strokeColor) => {
                setFormData(prev => ({ ...prev, strokeColor, color: strokeColor }))
                if (onColorSync) onColorSync({ strokeColor, color: strokeColor })
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: 'var(--local-text-secondary)', marginBottom: '4px', display: 'block' }}>
              선 두께 ({formData.strokeWidth}px)
            </label>
            <input
              type="range"
              min="0.5"
              max="10"
              step="0.5"
              value={formData.strokeWidth}
              onChange={(e) => {
                const strokeWidth = parseFloat(e.target.value)
                setFormData(prev => ({ ...prev, strokeWidth }))
                if (onColorSync) onColorSync({ strokeWidth })
              }}
              style={{ width: '100%', accentColor: 'var(--local-accent)' }}
            />
          </div>
        </div>
      )}

      {/* Asset kind 전용: 프리셋 아이콘 / 업로드 / 크기 / 색상 설정 */}
      {kind === 'asset' && (
      <>
      {/* Toggle button for preset icons */}
      <div style={{ marginBottom: '12px' }}>
        <button
          onClick={() => setShowIconSelector(!showIconSelector)}
          className={styles.smallButton}
          type="button"
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '13px',
            background: showIconSelector ? 'var(--local-accent)' : 'var(--local-surface-hover)',
            color: showIconSelector ? 'white' : 'var(--local-text)',
            border: '1px solid var(--local-border)',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          {showIconSelector ? '프리셋 아이콘 닫기' : '프리셋 아이콘 선택'}
        </button>
      </div>

      {/* Icon Selector */}
      {showIconSelector && (
        <IconSelector
          selectedIcon={formData.icon}
          onSelect={onIconSelect}
          onClose={() => setShowIconSelector(false)}
        />
      )}

      <AssetUploader
        previewUrl={previewUrl}
        assetFileName={assetFile?.name}
        onFileChange={onFileChange}
        onRemove={onRemoveFile}
        fileInputRef={fileInputRef}
      />

      {/* Show size controls when there's an actual image icon */}
      {hasActualImageIcon && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <label className={styles.label}>W (px)</label>
            <input
              type="number"
              min={4}
              max={200}
              value={formData.iconWidth}
              onChange={(e) => setFormData(prev => ({ ...prev, iconWidth: Math.max(4, Number(e.target.value) || 32) }))}
              className={styles.input}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className={styles.label}>H (px)</label>
            <input
              type="number"
              min={4}
              max={200}
              value={formData.iconHeight}
              onChange={(e) => setFormData(prev => ({ ...prev, iconHeight: Math.max(4, Number(e.target.value) || 32) }))}
              className={styles.input}
            />
          </div>
        </div>
      )}

      {/* Show color settings when there's no actual image icon */}
      {!hasActualImageIcon && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label className={styles.label}>색상 설정</label>

          {/* Stroke Color */}
          <div>
            <label style={{ fontSize: '12px', color: 'var(--local-text-secondary)', marginBottom: '4px', display: 'block' }}>선 색상</label>
            <ColorPicker
              value={formData.strokeColor}
              onChange={(strokeColor) => {
                setFormData(prev => ({ ...prev, strokeColor, color: strokeColor }))
                if (onColorSync) onColorSync({ strokeColor, color: strokeColor })
              }}
            />
          </div>

          {/* Fill Color */}
          <div>
            <label style={{ fontSize: '12px', color: 'var(--local-text-secondary)', marginBottom: '4px', display: 'block' }}>내부 색상</label>
            <ColorPicker
              value={formData.fillColor === 'none' ? '#000000' : formData.fillColor}
              onChange={(fillColor) => {
                setFormData(prev => ({ ...prev, fillColor, colorMode: 'fill' as const }))
                if (onColorSync) onColorSync({ fillColor, colorMode: 'fill' })
              }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--local-text)', cursor: 'pointer', marginTop: '4px' }}>
              <input
                type="checkbox"
                checked={formData.fillColor === 'none'}
                onChange={(e) => {
                  const newFill = e.target.checked ? 'none' : formData.strokeColor
                  const newMode = e.target.checked ? 'stroke' as const : 'fill' as const
                  setFormData(prev => ({ ...prev, fillColor: newFill, colorMode: newMode }))
                  if (onColorSync) onColorSync({ fillColor: newFill, colorMode: newMode })
                }}
                style={{ width: '14px', height: '14px', accentColor: 'var(--local-accent)' }}
              />
              채우기 없음 (선만)
            </label>
          </div>

          {/* Opacity */}
          <div>
            <label style={{ fontSize: '12px', color: 'var(--local-text-secondary)', marginBottom: '4px', display: 'block' }}>
              투명도 ({Math.round(formData.opacity * 100)}%)
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={formData.opacity}
              onChange={(e) => {
                const opacity = parseFloat(e.target.value)
                setFormData(prev => ({ ...prev, opacity }))
                if (onColorSync) onColorSync({ opacity })
              }}
              style={{ width: '100%', accentColor: 'var(--local-accent)' }}
            />
          </div>

          {/* Stroke Width */}
          <div>
            <label style={{ fontSize: '12px', color: 'var(--local-text-secondary)', marginBottom: '4px', display: 'block' }}>
              선 두께 ({formData.strokeWidth}px)
            </label>
            <input
              type="range"
              min="0"
              max="10"
              step="0.5"
              value={formData.strokeWidth}
              onChange={(e) => {
                const strokeWidth = parseFloat(e.target.value)
                setFormData(prev => ({ ...prev, strokeWidth }))
                if (onColorSync) onColorSync({ strokeWidth })
              }}
              style={{ width: '100%', accentColor: 'var(--local-accent)' }}
            />
          </div>
        </div>
      )}
      </>
      )}

      <PropertyEditor
        properties={formData.properties}
        onAdd={onAddProperty}
        onUpdate={onUpdateProperty}
        onRemove={onRemoveProperty}
        propertiesClassName={styles.properties}
        propertiesHeaderClassName={styles.propertiesHeader}
        smallButtonClassName={styles.smallButton}
        propertyClassName={styles.property}
        propertyInputClassName={styles.propertyInput}
        propertySelectClassName={styles.propertySelect}
        removeButtonClassName={styles.removeButton}
      />

      <div className={styles.formActions}>
        <button
          onClick={onSave}
          className={styles.saveButton}
        >
          저장
        </button>
        <button onClick={onCancel} className={styles.cancelButton}>
          취소
        </button>
      </div>

      <JsonPreview
        show={showJson}
        onToggle={() => setShowJson(!showJson)}
        data={getPreviewData()}
        jsonPreviewClassName={styles.jsonPreview}
        jsonContentClassName={styles.jsonContent}
      />
    </div>
  )
}
